import { useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TableDescriptor,
  Locale,
  TableQueryResult,
  ReferenceDisplayValue,
} from '@finance/contracts';
import type { TableDataSource } from './table-data-source.js';
import { runLocalTableQuery } from './table-data-source.js';
import type { TableState, TableSortState, TableFilterState, TableGroupState, TableAggregationState } from './table-state.js';
import { createSavedViewStatePatch, createTableInitialState, normalizePinnedColumns } from './table-state.js';
import { buildTableQuery } from './build-table-query.js';

export type TableDataMode = 'client' | 'server' | 'hybrid';

export interface UseTableDataOptions {
  descriptor: TableDescriptor;
  dataSource: TableDataSource;
  initialData?: Record<string, unknown>[];
  initialState?: Partial<TableState>;
  onError?: (error: Error) => void;
  getRowId?: (row: Record<string, unknown>) => string | undefined;
}

export interface UseTableDataReturn {
  state: TableState;
  data: Record<string, unknown>[];
  loading: boolean;
  error: Error | null;
  stale: boolean;
  degraded: boolean;
  totalCount: number;
  aggregates: Record<string, unknown> | null;
  groupMetadata: Record<string, unknown> | null;
  referenceDisplayMap: Record<string, Record<string, ReferenceDisplayValue>> | null;
  actions: TableActions;
}

export interface TableActions {
  setSorting: (sorting: TableSortState[]) => void;
  addSort: (field: string, direction: 'asc' | 'desc') => void;
  removeSort: (field: string) => void;
  clearSorting: () => void;
  setFilters: (filters: TableFilterState[]) => void;
  addFilter: (filter: TableFilterState) => void;
  removeFilter: (field: string) => void;
  clearFilters: () => void;
  setGlobalSearch: (search: string) => void;
  setGrouping: (grouping: TableGroupState[]) => void;
  addGroup: (field: string) => void;
  removeGroup: (field: string) => void;
  setAggregations: (aggregations: TableAggregationState[]) => void;
  setPagination: (pagination: { page: number; pageSize: number }) => void;
  setPageSize: (pageSize: number) => void;
  goToPage: (page: number) => void;
  setColumnVisibility: (columnId: string, visible: boolean) => void;
  setColumnOrder: (order: string[]) => void;
  setColumnSize: (columnId: string, width: number) => void;
  setPinnedColumns: (left: string[], right: string[]) => void;
  setDensity: (density: 'compact' | 'standard' | 'comfortable') => void;
  setSelection: (selectedIds: string[], selectAllMode?: 'page') => void;
  clearSelection: () => void;
  applySavedView: (viewState: Partial<TableState>) => void;
  resetFilters: () => void;
  resetLayout: () => void;
  resetAll: () => void;
  refresh: () => Promise<void>;
}

const DEBOUNCE_MS = 300;

export class TableRowIdentityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TableRowIdentityError';
  }
}

function shouldThrowRowIdentityError(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === 'test';
}

export function validateTableRows(
  descriptor: TableDescriptor,
  rows: Record<string, unknown>[],
  getRowId?: (row: Record<string, unknown>) => string | undefined,
): { rows: Record<string, unknown>[]; error: Error | null } {
  const seenIds = new Set<string>();
  const resolveRowId = (row: Record<string, unknown>) => {
    const raw = getRowId ? getRowId(row) : row[descriptor.rowIdField];
    return raw == null ? undefined : String(raw);
  };

  for (const [index, row] of rows.entries()) {
    const rowId = resolveRowId(row);
    if (!rowId) {
      const error = new TableRowIdentityError(
        `Table ${String(descriptor.id)} row ${index} is missing required identity field ${descriptor.rowIdField}`,
      );
      if (shouldThrowRowIdentityError()) {
        throw error;
      }
      return { rows: [], error };
    }
    if (seenIds.has(rowId)) {
      const error = new TableRowIdentityError(
        `Table ${String(descriptor.id)} contains duplicate row identity ${rowId}`,
      );
      if (shouldThrowRowIdentityError()) {
        throw error;
      }
      return { rows: [], error };
    }
    seenIds.add(rowId);
  }

  return { rows, error: null };
}

export type TableAction =
  | { type: 'SET_SORTING'; payload: TableSortState[] }
  | { type: 'ADD_SORT'; payload: { field: string; direction: 'asc' | 'desc' } }
  | { type: 'REMOVE_SORT'; payload: string }
  | { type: 'CLEAR_SORTING' }
  | { type: 'SET_FILTERS'; payload: TableFilterState[] }
  | { type: 'ADD_FILTER'; payload: TableFilterState }
  | { type: 'REMOVE_FILTER'; payload: string }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_GLOBAL_SEARCH'; payload: string }
  | { type: 'SET_GROUPING'; payload: TableGroupState[] }
  | { type: 'ADD_GROUP'; payload: string }
  | { type: 'REMOVE_GROUP'; payload: string }
  | { type: 'SET_AGGREGATIONS'; payload: TableAggregationState[] }
  | { type: 'SET_PAGINATION'; payload: { page: number; pageSize: number } }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'GO_TO_PAGE'; payload: number }
  | { type: 'SET_COLUMN_VISIBILITY'; payload: { columnId: string; visible: boolean } }
  | { type: 'SET_COLUMN_ORDER'; payload: string[] }
  | { type: 'SET_COLUMN_SIZE'; payload: { columnId: string; width: number } }
  | { type: 'SET_PINNED_COLUMNS'; payload: { left: string[]; right: string[] } }
  | { type: 'SET_DENSITY'; payload: 'compact' | 'standard' | 'comfortable' }
  | { type: 'SET_SELECTION'; payload: { selectedIds: string[]; selectAllMode?: 'page' } }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'APPLY_SAVED_VIEW'; payload: Partial<TableState> }
  | { type: 'RESET_FILTERS' }
  | { type: 'RESET_LAYOUT' }
  | { type: 'RESET_ALL' }
  | { type: 'SET_DATA'; payload: { data: TableQueryResult; resetPage?: boolean } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: Error | null }
  | { type: 'SET_STALE'; payload: boolean }
  | { type: 'SET_DEGRADED'; payload: boolean }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean };

function getPinnableVisibleColumns(
  descriptor: TableDescriptor,
  tableState: TableState,
): { pinnable: Set<string>; visible: Set<string> } {
  const pinnable = new Set(descriptor.columns.filter((column) => column.pinnable).map((column) => column.id));
  const requiredVisible = new Set(
    descriptor.columns.filter((column) => column.requiredVisible).map((column) => column.id),
  );
  const visible = new Set(
    tableState.columnVisibility
      .filter((entry) => entry.visible || requiredVisible.has(entry.columnId))
      .map((entry) => entry.columnId),
  );
  return { pinnable, visible };
}

export function tableReducer(state: TableReducerState, action: TableAction): TableReducerState {
  switch (action.type) {
    case 'SET_SORTING':
      return { ...state, tableState: { ...state.tableState, sorting: action.payload, pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'ADD_SORT':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          sorting: [...state.tableState.sorting.filter((s) => s.field !== action.payload.field), action.payload],
          pagination: { ...state.tableState.pagination, page: 0 },
        },
      };
    case 'REMOVE_SORT':
      return {
        ...state,
        tableState: { ...state.tableState, sorting: state.tableState.sorting.filter((s) => s.field !== action.payload), pagination: { ...state.tableState.pagination, page: 0 } },
      };
    case 'CLEAR_SORTING':
      return { ...state, tableState: { ...state.tableState, sorting: [], pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'SET_FILTERS':
      return { ...state, tableState: { ...state.tableState, filters: action.payload, pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'ADD_FILTER':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          filters: [...state.tableState.filters.filter((f) => f.field !== action.payload.field), action.payload],
          pagination: { ...state.tableState.pagination, page: 0 },
        },
      };
    case 'REMOVE_FILTER':
      return {
        ...state,
        tableState: { ...state.tableState, filters: state.tableState.filters.filter((f) => f.field !== action.payload), pagination: { ...state.tableState.pagination, page: 0 } },
      };
    case 'CLEAR_FILTERS':
      return { ...state, tableState: { ...state.tableState, filters: [], pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'SET_GLOBAL_SEARCH':
      return { ...state, tableState: { ...state.tableState, globalSearch: action.payload, pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'SET_GROUPING':
      return { ...state, tableState: { ...state.tableState, grouping: action.payload, pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'ADD_GROUP':
      return { ...state, tableState: { ...state.tableState, grouping: [...state.tableState.grouping, { field: action.payload, direction: 'asc' }], pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'REMOVE_GROUP':
      return { ...state, tableState: { ...state.tableState, grouping: state.tableState.grouping.filter((g) => g.field !== action.payload), pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'SET_AGGREGATIONS':
      return { ...state, tableState: { ...state.tableState, aggregations: action.payload } };
    case 'SET_PAGINATION':
      return { ...state, tableState: { ...state.tableState, pagination: action.payload } };
    case 'SET_PAGE_SIZE':
      return { ...state, tableState: { ...state.tableState, pagination: { ...state.tableState.pagination, pageSize: action.payload, page: 0 } } };
    case 'GO_TO_PAGE':
      return { ...state, tableState: { ...state.tableState, pagination: { ...state.tableState.pagination, page: action.payload } } };
    case 'SET_COLUMN_VISIBILITY':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          columnVisibility: state.tableState.columnVisibility.map((cv) =>
            cv.columnId === action.payload.columnId ? { ...cv, visible: action.payload.visible } : cv
          ),
        },
      };
    case 'SET_COLUMN_ORDER':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          columnOrder: action.payload.map((id, index) => ({ columnId: id, order: index })),
        },
      };
    case 'SET_COLUMN_SIZE':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          columnSizes: state.tableState.columnSizes.map((cs) =>
            cs.columnId === action.payload.columnId ? { ...cs, width: action.payload.width } : cs
          ),
        },
      };
    case 'SET_PINNED_COLUMNS':
      return { ...state, tableState: { ...state.tableState, pinnedColumns: action.payload } };
    case 'SET_DENSITY':
      return { ...state, tableState: { ...state.tableState, density: action.payload } };
    case 'SET_SELECTION':
      return {
        ...state,
        tableState: { ...state.tableState, selection: { selectedIds: action.payload.selectedIds, selectAllMode: action.payload.selectAllMode ?? 'page' } },
      };
    case 'CLEAR_SELECTION':
      return { ...state, tableState: { ...state.tableState, selection: { selectedIds: [], selectAllMode: 'page' } } };
    case 'APPLY_SAVED_VIEW':
      return { ...state, tableState: { ...state.tableState, ...action.payload, hasUnsavedChanges: false } };
    case 'RESET_FILTERS':
      return { ...state, tableState: { ...state.tableState, filters: [], globalSearch: '', pagination: { ...state.tableState.pagination, page: 0 } } };
    case 'RESET_LAYOUT':
      return { ...state, tableState: { ...state.tableState, columnVisibility: state.initialLayout.columnVisibility, columnOrder: state.initialLayout.columnOrder, columnSizes: state.initialLayout.columnSizes, pinnedColumns: state.initialLayout.pinnedColumns } };
    case 'RESET_ALL':
      return { ...state, tableState: state.initialTableState, dataState: state.initialDataState };
    case 'SET_DATA':
      return {
        ...state,
        dataState: {
          ...state.dataState,
          data: action.payload.data.rows,
          totalCount: action.payload.data.pagination.total ?? action.payload.data.rows.length,
          aggregates: action.payload.data.aggregates ?? null,
          groupMetadata: action.payload.data.groupMetadata ?? null,
          referenceDisplayMap: action.payload.data.referenceDisplayMap ?? null,
          stale: action.payload.data.stale ?? false,
          degraded: action.payload.data.degraded ?? false,
          dataTimestamp: action.payload.data.dataTimestamp,
        },
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_STALE':
      return { ...state, dataState: { ...state.dataState, stale: action.payload } };
    case 'SET_DEGRADED':
      return { ...state, dataState: { ...state.dataState, degraded: action.payload } };
    case 'SET_UNSAVED_CHANGES':
      return { ...state, tableState: { ...state.tableState, hasUnsavedChanges: action.payload } };
    default:
      return state;
  }
}

export interface TableReducerState {
  tableState: TableState;
  dataState: {
    data: Record<string, unknown>[];
    totalCount: number;
    aggregates: Record<string, unknown> | null;
    groupMetadata: Record<string, unknown> | null;
    referenceDisplayMap: Record<string, Record<string, ReferenceDisplayValue>> | null;
    stale: boolean;
    degraded: boolean;
    dataTimestamp: number;
  };
  loading: boolean;
  error: Error | null;
  initialTableState: TableState;
  initialDataState: TableReducerState['dataState'];
  initialLayout: {
    columnVisibility: TableState['columnVisibility'];
    columnOrder: TableState['columnOrder'];
    columnSizes: TableState['columnSizes'];
    pinnedColumns: TableState['pinnedColumns'];
  };
}

export function useTableData({
  descriptor,
  dataSource,
  initialData,
  initialState,
  onError,
  getRowId,
}: UseTableDataOptions): UseTableDataReturn {
  const { i18n } = useTranslation();
  const initialTableState = useMemo(() => createTableInitialState(descriptor, undefined, initialState), [descriptor, initialState]);
  const buildCurrentQuery = useCallback(
    (tableState: TableState) =>
      buildTableQuery(
        descriptor,
        { ...tableState, referenceInclusion: [] },
        i18n.language as Locale,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      ),
    [descriptor, i18n.language],
  );
  const validatedInitialData = useMemo(
    () => {
      if (initialData === undefined) {
        return validateTableRows(descriptor, [], getRowId);
      }
      const localResult = runLocalTableQuery(initialData, buildCurrentQuery(initialTableState));
      return validateTableRows(descriptor, localResult.rows, getRowId);
    },
    [descriptor, initialData, getRowId, buildCurrentQuery, initialTableState],
  );
  const initialDataState = useMemo(
    () => ({
      data: validatedInitialData.rows,
      totalCount: validatedInitialData.rows.length,
      aggregates: null,
      groupMetadata: null,
      referenceDisplayMap: null,
      stale: false,
      degraded: false,
      dataTimestamp: 0,
    }),
    [validatedInitialData]
  );

  const [state, dispatch] = useReducer(tableReducer, {
    tableState: initialTableState,
    dataState: initialDataState,
    loading: false,
    error: validatedInitialData.error,
    initialTableState,
    initialDataState,
    initialLayout: {
      columnVisibility: initialTableState.columnVisibility,
      columnOrder: initialTableState.columnOrder,
      columnSizes: initialTableState.columnSizes,
      pinnedColumns: initialTableState.pinnedColumns,
    },
  });

  const queryVersion = useRef(0);
  const tableStateRef = useRef(state.tableState);
  tableStateRef.current = state.tableState;

  const prevSearchRef = useRef(state.tableState.globalSearch);
  const prevFiltersRef = useRef(state.tableState.filters);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    const currentVersion = ++queryVersion.current;
    if (initialData !== undefined) {
      const localResult = runLocalTableQuery(initialData, buildCurrentQuery(tableStateRef.current));
      const validated = validateTableRows(descriptor, localResult.rows, getRowId);
      dispatch({
        type: 'SET_DATA',
        payload: {
          data: {
            ...localResult,
            rows: validated.rows,
          },
        },
      });
      if (validated.error) {
        dispatch({ type: 'SET_ERROR', payload: validated.error });
      }
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const query = buildCurrentQuery(tableStateRef.current);
      const result = await dataSource.fetch(query, signal);
      const validated = validateTableRows(descriptor, result.rows, getRowId);

      if (signal?.aborted || currentVersion !== queryVersion.current) return;

      dispatch({
        type: 'SET_DATA',
        payload: {
          data: {
            ...result,
            rows: validated.rows,
          },
        },
      });
      if (validated.error) {
        dispatch({ type: 'SET_ERROR', payload: validated.error });
      }
    } catch (err) {
      if (signal?.aborted || currentVersion !== queryVersion.current) return;
      const error = err instanceof Error ? err : new Error(String(err));
      dispatch({ type: 'SET_ERROR', payload: error });
      onError?.(error);
    }
  }, [descriptor, dataSource, onError, getRowId, initialData, buildCurrentQuery]);

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    const controller = new AbortController();

    const searchOrFilterChanged =
      prevSearchRef.current !== state.tableState.globalSearch ||
      prevFiltersRef.current !== state.tableState.filters;
    prevSearchRef.current = state.tableState.globalSearch;
    prevFiltersRef.current = state.tableState.filters;

    if (initialData !== undefined) {
      void fetchDataRef.current(controller.signal);
      return () => controller.abort();
    }

    if (searchOrFilterChanged) {
      const timer = setTimeout(() => fetchDataRef.current(controller.signal), DEBOUNCE_MS);
      return () => {
        clearTimeout(timer);
        controller.abort();
      };
    }

    void fetchDataRef.current(controller.signal);
    return () => controller.abort();
  }, [
    state.tableState.sorting,
    state.tableState.filters,
    state.tableState.globalSearch,
    state.tableState.grouping,
    state.tableState.aggregations,
    state.tableState.pagination,
    initialData,
  ]);

  const actions: TableActions = useMemo(
    () => ({
      setSorting: (sorting) => dispatch({ type: 'SET_SORTING', payload: sorting }),
      addSort: (field, direction) => dispatch({ type: 'ADD_SORT', payload: { field, direction } }),
      removeSort: (field) => dispatch({ type: 'REMOVE_SORT', payload: field }),
      clearSorting: () => dispatch({ type: 'CLEAR_SORTING' }),
      setFilters: (filters) => dispatch({ type: 'SET_FILTERS', payload: filters }),
      addFilter: (filter) => dispatch({ type: 'ADD_FILTER', payload: filter }),
      removeFilter: (field) => dispatch({ type: 'REMOVE_FILTER', payload: field }),
      clearFilters: () => dispatch({ type: 'CLEAR_FILTERS' }),
      setGlobalSearch: (search) => dispatch({ type: 'SET_GLOBAL_SEARCH', payload: search }),
      setGrouping: (grouping) => dispatch({ type: 'SET_GROUPING', payload: grouping }),
      addGroup: (field) => dispatch({ type: 'ADD_GROUP', payload: field }),
      removeGroup: (field) => dispatch({ type: 'REMOVE_GROUP', payload: field }),
      setAggregations: (aggregations) => dispatch({ type: 'SET_AGGREGATIONS', payload: aggregations }),
      setPagination: (pagination) => dispatch({ type: 'SET_PAGINATION', payload: pagination }),
      setPageSize: (pageSize) => dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize }),
      goToPage: (page) => dispatch({ type: 'GO_TO_PAGE', payload: page }),
      setColumnVisibility: (columnId, visible) => dispatch({ type: 'SET_COLUMN_VISIBILITY', payload: { columnId, visible } }),
      setColumnOrder: (order) => dispatch({ type: 'SET_COLUMN_ORDER', payload: order }),
      setColumnSize: (columnId, width) => dispatch({ type: 'SET_COLUMN_SIZE', payload: { columnId, width } }),
      setPinnedColumns: (left, right) => {
        const { pinnable, visible } = getPinnableVisibleColumns(descriptor, tableStateRef.current);
        const normalized = normalizePinnedColumns({ left, right }, { allowedIds: pinnable, visibleIds: visible });
        dispatch({ type: 'SET_PINNED_COLUMNS', payload: normalized });
      },
      setDensity: (density) => dispatch({ type: 'SET_DENSITY', payload: density }),
      setSelection: (selectedIds, selectAllMode) => dispatch({ type: 'SET_SELECTION', payload: { selectedIds, selectAllMode } }),
      clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
      applySavedView: (viewState) => {
        const nextState: TableState = { ...tableStateRef.current, ...viewState };
        const { pinnable, visible } = getPinnableVisibleColumns(descriptor, nextState);
        const normalizedPinned = normalizePinnedColumns(nextState.pinnedColumns, {
          allowedIds: pinnable,
          visibleIds: visible,
        });
        dispatch({ type: 'APPLY_SAVED_VIEW', payload: { ...viewState, pinnedColumns: normalizedPinned } });
      },
      resetFilters: () => dispatch({ type: 'RESET_FILTERS' }),
      resetLayout: () => dispatch({ type: 'RESET_LAYOUT' }),
      resetAll: () => dispatch({ type: 'RESET_ALL' }),
      refresh: async () => {
        queryVersion.current++;
        await fetchDataRef.current();
      },
    }),
    [descriptor]
  );

  const localComputedResult = useMemo(() => {
    if (initialData === undefined) {
      return null;
    }
    const result = runLocalTableQuery(initialData, buildCurrentQuery(state.tableState));
    const validated = validateTableRows(descriptor, result.rows, getRowId);
    return {
      ...result,
      rows: validated.rows,
      error: validated.error,
    };
  }, [initialData, buildCurrentQuery, state.tableState, descriptor, getRowId]);

  return {
    state: state.tableState,
    data: localComputedResult?.rows ?? state.dataState.data,
    loading: initialData === undefined ? state.loading : false,
    error: localComputedResult?.error ?? state.error,
    stale: localComputedResult?.stale ?? state.dataState.stale,
    degraded: localComputedResult?.degraded ?? state.dataState.degraded,
    totalCount: localComputedResult?.pagination.total ?? state.dataState.totalCount,
    aggregates: localComputedResult?.aggregates ?? state.dataState.aggregates,
    groupMetadata: localComputedResult?.groupMetadata ?? state.dataState.groupMetadata,
    referenceDisplayMap: localComputedResult?.referenceDisplayMap ?? state.dataState.referenceDisplayMap,
    actions,
  };
}